import React from 'react';
import { Box } from '@mui/material';
import { HighlightChessboard, ThemeProvider, type BoardThemeId } from 'react-chess-core';
import { nonInteractivePreviewBoardShellSx } from './previewBoardShell';

const MOVE_ANIMATION_MS = 220;

type LayeredAnimatedPreviewBoardProps = {
  fens: string[];
  ply: number;
  boardWidth: number;
  boardOrientation: 'white' | 'black';
  boardTheme: BoardThemeId;
  animate?: boolean;
  baseLastMoveUci?: string | null;
  overlayLastMoveUci?: string | null;
};

const LayeredAnimatedPreviewBoard = ({
  fens,
  ply,
  boardWidth,
  boardOrientation,
  boardTheme,
  animate = true,
  baseLastMoveUci = null,
  overlayLastMoveUci = null,
}: LayeredAnimatedPreviewBoardProps) => {
  const initialFen = fens[0]!;
  const overlayFen = fens[ply] ?? initialFen;
  const showOverlay = animate && ply > 0;

  const boardProps = {
    boardWidth,
    boardOrientation,
    arePiecesDraggable: false,
    showBoardNotation: false,
    checkSquare: '',
    hintSquare: null,
    incorrectMoveSquare: null,
  } as const;

  return (
    <ThemeProvider theme="dark" boardTheme={boardTheme}>
      <Box sx={nonInteractivePreviewBoardShellSx(boardWidth)}>
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <HighlightChessboard
            {...boardProps}
            position={initialFen}
            animationDuration={0}
            lastMoveUci={baseLastMoveUci}
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            visibility: showOverlay ? 'visible' : 'hidden',
          }}
        >
          <HighlightChessboard
            {...boardProps}
            position={overlayFen}
            animationDuration={showOverlay ? MOVE_ANIMATION_MS : 0}
            lastMoveUci={overlayLastMoveUci}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LayeredAnimatedPreviewBoard;
